<div class="fixed inset-0 z-50">
  <button
    type="button"
    class="fixed inset-0 z-10 w-dvw overflow-hidden green-noise-bg"
    aria-label="Close exit survey"
    (click)="close()"
  ></button>

  <div class="pointer-events-none absolute inset-0 z-20 pl-10 focus:outline-none sm:pl-16">
    <section
      class="pointer-events-auto relative ml-auto flex h-full w-full max-w-2xl flex-col p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancellation-detail-title"
      (click)="$event.stopPropagation()"
    >
      <div class="relative flex h-full flex-col overflow-hidden shadow-xl rounded-xl">
        <div class="flex h-full flex-col overflow-hidden divide-y divide-border">
          <header class="px-2 py-4 drawer-header-primary">
            <div class="flex h-full items-center space-x-3">
              @if (detail(); as detail) {
              <span class="size-11 shrink-0 overflow-hidden rounded-full bg-zinc-100 dark:bg-white/5">
                <atom-ldg-img
                  inputClass="object-cover"
                  w="full"
                  h="full"
                  radius="full"
                  [src]="imageService.getImageSource(detail.account.logoUrl, 'gallery')"
                />
              </span>
              <div class="min-w-0 flex-1 px-2">
                <h2 id="cancellation-detail-title" class="m-0 truncate text-md font-semibold text-primary">
                  {{ detail.account.name ?? 'Unknown account' }}
                </h2>
                <p class="m-0 mt-1 flex flex-wrap items-center gap-2 text-sm text-secondary">
                  <span
                    class="inline-flex items-center rounded-md px-2 py-0.5 font-medium ring-1 ring-inset"
                    [class]="outcomeChipClass(detail.outcome)"
                  >
                    {{ outcomeLabel(detail.outcome) }}
                  </span>
                  <span>{{ scopeLabel(detail.scope) }}</span>
                  <span aria-hidden="true">·</span>
                  <span>{{ detail.createdDate | date: 'dd MMM yyyy, HH:mm' }}</span>
                </p>
              </div>
              } @else {
              <h2 id="cancellation-detail-title" class="m-0 min-w-0 flex-1 px-2 text-md font-semibold text-primary">
                Exit survey
              </h2>
              }

              <div class="flex h-full flex-col items-end justify-between">
                <button type="button" class="fab-button-secondary text-secondary" aria-label="Close" (click)="close()">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="size-5"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </header>

          <div class="relative h-full min-h-0 flex-1 overflow-hidden drawer-body-primary">
            <div class="h-full overflow-y-auto scrollbar-transparent p-5">
      @switch (loadingState()) {
      @case ('loading') {
      <atom-loading-inline title="Loading response" message="Fetching the exit survey…" />
      }
      @case ('error') {
      <div class="rounded-xl border border-rose-200 bg-rose-50 p-3 dark:border-rose-500/30 dark:bg-rose-500/10" role="alert">
        <p class="m-0 text-sm text-rose-700 dark:text-rose-300">{{ error() }}</p>
      </div>
      }
      @default {
      @if (detail(); as detail) {
      <div class="flex flex-col gap-6">
        <!-- Who -->
        <section>
          <h3 class="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">Who</h3>
          <dl class="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt class="text-xs text-zinc-500 dark:text-zinc-500">Cancelled by</dt>
              <dd class="m-0 mt-0.5 text-zinc-900 dark:text-zinc-100">
                @if (userLink(); as link) {
                <a [routerLink]="link" class="text-violet-700 hover:underline dark:text-violet-300">
                  {{ detail.account.userName ?? detail.account.userEmail ?? 'Unknown user' }}
                </a>
                } @else {
                {{ detail.account.userName ?? detail.account.userEmail ?? '—' }}
                }
              </dd>
            </div>
            <div>
              <dt class="text-xs text-zinc-500 dark:text-zinc-500">Account</dt>
              <dd class="m-0 mt-0.5 text-zinc-900 dark:text-zinc-100">
                @if (profileLink(); as link) {
                <a [routerLink]="link" class="text-violet-700 hover:underline dark:text-violet-300">
                  {{ detail.account.name ?? 'Open profile' }}
                </a>
                } @else {
                {{ detail.account.name ?? '—' }}
                }
              </dd>
            </div>
          </dl>
        </section>

        <!-- Their answers -->
        <section>
          <h3 class="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
            Their answers
          </h3>
          <p class="m-0 mt-1 text-xs text-zinc-500 dark:text-zinc-500">
            Shown with the wording that was on screen when they answered.
          </p>

          @if (listedAnswers().length > 0) {
          <dl class="mt-3 flex flex-col gap-3">
            @for (answer of listedAnswers(); track $index) {
            <div class="rounded-xl border border-zinc-200 p-3 dark:border-white/10">
              <dt class="text-xs text-zinc-500 dark:text-zinc-400">{{ answer.questionText ?? answer.questionId }}</dt>
              <dd class="m-0 mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {{ answerText(answer) }}
              </dd>
            </div>
            }
          </dl>
          } @else {
          <p class="m-0 mt-3 text-sm text-zinc-500">No answers were recorded.</p>
          }

          @if (commentAnswer(); as comment) {
          <div class="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 dark:border-white/10 dark:bg-white/5">
            <p class="m-0 text-xs text-zinc-500 dark:text-zinc-400">{{ comment.questionText }}</p>
            <p class="m-0 mt-1 text-sm leading-relaxed text-zinc-800 dark:text-zinc-200">
              “{{ comment.answerValue }}”
            </p>
          </div>
          }
        </section>

        <!-- Billing -->
        <section>
          <h3 class="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">Billing</h3>
          <dl class="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt class="text-xs text-zinc-500 dark:text-zinc-500">Subscription value</dt>
              <dd class="m-0 mt-0.5 tabular-nums text-zinc-900 dark:text-zinc-100">{{ renewalLabel() }}</dd>
            </div>
            <div>
              <dt class="text-xs text-zinc-500 dark:text-zinc-500">Access ends</dt>
              <dd class="m-0 mt-0.5 text-zinc-900 dark:text-zinc-100">
                {{ detail.accessEndsAt ? (detail.accessEndsAt | date: 'dd MMM yyyy') : '—' }}
              </dd>
            </div>
            @if (detail.pauseResumesAt) {
            <div>
              <dt class="text-xs text-zinc-500 dark:text-zinc-500">Pause resumes</dt>
              <dd class="m-0 mt-0.5 text-zinc-900 dark:text-zinc-100">
                {{ detail.pauseResumesAt | date: 'dd MMM yyyy' }}
              </dd>
            </div>
            }
            <div>
              <dt class="text-xs text-zinc-500 dark:text-zinc-500">Stripe feedback sent</dt>
              <dd class="m-0 mt-0.5 text-zinc-900 dark:text-zinc-100">{{ detail.stripeFeedback ?? '—' }}</dd>
            </div>
          </dl>
          @if (detail.renewalAmountMinorUnits === null) {
          <p class="m-0 mt-2 text-xs text-zinc-500 dark:text-zinc-500">
            This off-boarding predates subscription-value capture, so no price was
            recorded. Stripe no longer reports the plan a cancelled subscription
            was on, so it cannot be filled in retrospectively.
          </p>
          }
          <div class="mt-3 flex flex-wrap gap-4">
            @if (detail.stripeCustomerId) {
            <atom-copy-id [value]="detail.stripeCustomerId" label="Stripe customer" />
            }
            @if (detail.stripeSubscriptionId) {
            <atom-copy-id [value]="detail.stripeSubscriptionId" label="Subscription" />
            }
          </div>
        </section>

        <!-- Obligations -->
        <section>
          <h3 class="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
            Creator obligations
          </h3>
          <dl class="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt class="text-xs text-zinc-500 dark:text-zinc-500">Still payable</dt>
              <dd class="m-0 mt-0.5 tabular-nums text-zinc-900 dark:text-zinc-100">{{ money(detail.payableMinorUnits) }}</dd>
            </div>
            <div>
              <dt class="text-xs text-zinc-500 dark:text-zinc-500">Removed by un-hiring</dt>
              <dd class="m-0 mt-0.5 tabular-nums text-zinc-900 dark:text-zinc-100">{{ money(detail.removedMinorUnits) }}</dd>
            </div>
          </dl>

          @if (detail.unhires.length > 0) {
          <ul class="mt-3 flex flex-col divide-y divide-zinc-100 dark:divide-white/5">
            @for (unhire of detail.unhires; track unhire.briefApplicationId) {
            <li class="flex items-center gap-3 py-2">
              <span class="size-8 shrink-0 overflow-hidden rounded-full bg-zinc-100 dark:bg-white/5">
                <atom-ldg-img
                  inputClass="object-cover"
                  w="full"
                  h="full"
                  radius="full"
                  [src]="imageService.getImageSource(unhire.creatorPhotoURL, 'gallery')"
                />
              </span>
              <span class="min-w-0 flex-1 truncate text-sm text-zinc-900 dark:text-zinc-100">
                {{ unhire.creatorName ?? unhire.creatorUserId }}
              </span>
              @if (!unhire.walletReservationReleased) {
              <span class="shrink-0 text-xs text-zinc-500 dark:text-zinc-500">no reservation held</span>
              }
              <span class="shrink-0 tabular-nums text-sm text-zinc-600 dark:text-zinc-400">
                {{ money(unhire.feeMinorUnits) }}
              </span>
            </li>
            }
          </ul>
          } @else {
          <p class="m-0 mt-3 text-sm text-zinc-500">No creators were un-hired.</p>
          }
        </section>

        <!-- Acknowledgements -->
        @if (detail.acknowledgements?.length) {
        <section>
          <h3 class="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
            Acknowledged
          </h3>
          <p class="m-0 mt-1 text-xs text-zinc-500 dark:text-zinc-500">
            {{ detail.acknowledgedAt ? (detail.acknowledgedAt | date: 'dd MMM yyyy, HH:mm') : 'Date not recorded' }}
          </p>
          <ul class="mt-2 flex flex-wrap gap-2">
            @for (key of detail.acknowledgements; track key) {
            <li class="rounded-md bg-zinc-100 px-2 py-0.5 text-xs text-zinc-700 dark:bg-white/5 dark:text-zinc-300">
              {{ key }}
            </li>
            }
          </ul>
        </section>
        }

        @if (detail.obligationsSnapshot) {
        <details class="rounded-xl border border-zinc-200 p-3 dark:border-white/10">
          <summary class="cursor-pointer text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
            Obligations snapshot
          </summary>
          <pre class="mt-2 overflow-x-auto text-xs text-zinc-600 dark:text-zinc-400">{{ detail.obligationsSnapshot | json }}</pre>
        </details>
        }
      </div>
      }
      }
      }
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</div>
